const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: Button } = require('@mui/material/Button');
const { default: Alert } = require('@mui/material/Alert');

const FullScreenDialog = require('containers/FullScreenDialog');
const useForm = require('hooks/useForm');
const UserSearchFilterFormConfig = require('./formConfig');
const InvalidSubmitFocusUtils = require('hooks/useFormPrimitives/invalidSubmitFocusUtils');

const internals = {};

const { FORM_ID } = UserSearchFilterFormConfig;

module.exports = function UserSearchFilterDialog(props) {

    const {
        onRequestClose,
        onSubmit,
        resetSearch,
        open,
        interests: interestsList = [],
        badges = [],
        discoverSortType,
        majors = [],
        canSearchHometown,
        canSearchHousing,
        departments = [],
        officesList = [],
        yearsHiredList = [],
        isCompany,
        isCommunity,
        isSchoolOnline,
        classId,
        discoverSearchFilter,
        activeSearchFilter,
        incomingClassList = [],
        graduatingClassList = [],
        isBlankSearch,
        roleGroupFilter,
        currentUserPermissions = {},
        currentUserRole,
        availableRoleGroups = [],
        schoolRoles = [],
        majorAsArray = true,
        includeContextFilterType = true,
        fieldNames = UserSearchFilterFormConfig.DEFAULT_FIELD_NAMES
    } = props;

    const allRoles = internals.asArray(props.allRoles).filter(Boolean);

    const roleOptions = React.useMemo(() => {

        const options = internals.asArray(availableRoleGroups).length ? availableRoleGroups : schoolRoles;
        return internals.asArray(options).filter(Boolean);
    }, [availableRoleGroups, schoolRoles]);

    const searchFilter = discoverSearchFilter || activeSearchFilter || {};

    const defaults = React.useMemo(() => internals.buildDefaults({
        currentUserRole,
        interestsList,
        isBlankSearch,
        roleGroupFilter,
        roleOptions,
        searchFilter
    }), [
        currentUserRole,
        interestsList,
        isBlankSearch,
        roleGroupFilter,
        roleOptions,
        searchFilter
    ]);

    const [selectedRoleGroup, setSelectedRoleGroup] = React.useState(defaults.contextFilter || '');
    const [selectedLocationType, setSelectedLocationType] = React.useState(defaults.locationType || '');
    const [submitError, setSubmitError] = React.useState('');
    const wasOpenRef = React.useRef(false);
    const formContainerRef = React.useRef(null);
    const methodsRef = React.useRef(null);
    const handleLocationTypeChange = React.useCallback((nextLocationType) => {

        setSelectedLocationType(nextLocationType);
        internals.clearCustomLocationWhenInactive({
            methods: methodsRef.current,
            selectedLocationType: nextLocationType
        });
    }, []);

    const usesRoleGroups = internals.asArray(availableRoleGroups).length > 0;

    const roleContext = React.useMemo(() => internals.buildRoleContext({
        allRoles,
        badges,
        departments,
        roleOptions,
        selectedRoleGroup,
        usesRoleGroups
    }), [
        allRoles,
        badges,
        departments,
        roleOptions,
        selectedRoleGroup,
        usesRoleGroups
    ]);

    const formConfig = React.useMemo(() => UserSearchFilterFormConfig.buildFormConfig({
        canSearchHometown,
        canSearchHousing,
        currentUserPermissions,
        defaults,
        fieldNames,
        fieldProps: internals.fieldProps,
        graduatingClassList,
        incomingClassList,
        interestsList,
        isCompany,
        isCommunity,
        isSchoolOnline,
        majors,
        officesList,
        onSubmit: async (values) => {

            const validationErrors = internals.getValidationErrors(values);

            if (validationErrors.length) {
                internals.applyValidationErrors({
                    formEl: formContainerRef.current,
                    methods: methodsRef.current,
                    validationErrors
                });
                setSubmitError('');
                return false;
            }

            methodsRef.current?.clearErrors();
            setSubmitError('');

            const searchDetails = internals.buildSearchPayload({
                classId,
                includeContextFilterType,
                majorAsArray,
                usesRoleGroups,
                values
            });

            const result = internals.isDefaultSearchPayload(searchDetails)
                ? await resetSearch(discoverSortType, searchDetails.contextFilter)
                : await onSubmit(searchDetails, discoverSortType);

            if (result) {
                onRequestClose();
            }

            return result;
        },
        renderInterestsHeader: () => <internals.LocationHeader>Interests</internals.LocationHeader>,
        renderLocationHeader: () => <internals.LocationHeader>Location</internals.LocationHeader>,
        roleContext,
        roleOptions,
        selectedLocationType,
        selectedRoleGroup,
        setSelectedLocationType: handleLocationTypeChange,
        setSelectedRoleGroup,
        yearsHiredList
    }), [
        canSearchHometown,
        canSearchHousing,
        classId,
        currentUserPermissions,
        defaults,
        discoverSortType,
        fieldNames,
        graduatingClassList,
        handleLocationTypeChange,
        includeContextFilterType,
        incomingClassList,
        interestsList,
        isCompany,
        isCommunity,
        isSchoolOnline,
        majorAsArray,
        majors,
        officesList,
        onRequestClose,
        onSubmit,
        resetSearch,
        roleContext,
        roleOptions,
        selectedLocationType,
        selectedRoleGroup,
        usesRoleGroups,
        yearsHiredList
    ]);

    const {
        methods,
        Form
    } = useForm(formConfig);

    methodsRef.current = methods;

    React.useEffect(() => {

        internals.clearCustomLocationWhenInactive({
            methods,
            selectedLocationType
        });
    }, [methods, selectedLocationType]);

    React.useEffect(() => {

        if (!open) {
            wasOpenRef.current = false;
            return;
        }

        if (wasOpenRef.current) {
            return;
        }

        wasOpenRef.current = true;
        methods.reset(defaults);
        setSelectedRoleGroup(defaults.contextFilter || '');
        setSelectedLocationType(defaults.locationType || '');
        setSubmitError('');
    }, [open, defaults, methods]);

    const clearSearch = async () => {

        const clearedDefaults = internals.buildDefaults({
            currentUserRole,
            interestsList,
            isBlankSearch: true,
            roleGroupFilter,
            roleOptions,
            searchFilter: {}
        });

        methods.reset(clearedDefaults);
        setSelectedRoleGroup(clearedDefaults.contextFilter || '');
        setSubmitError('');
        await resetSearch(discoverSortType);
        onRequestClose();
    };

    return <FullScreenDialog
        title='Search'
        open={open}
        onRequestClose={onRequestClose}
    >
        <internals.FormWrapper
            ref={formContainerRef}
            onKeyDownCapture={(event) => {

                if (event.key !== 'Enter' || event.defaultPrevented) {
                    return;
                }

                const target = event.target;

                if (!(target instanceof HTMLElement) || target.tagName !== 'INPUT') {
                    return;
                }

                if (target.getAttribute('aria-expanded') === 'true' || target.closest('[role="listbox"]')) {
                    return;
                }

                const formEl = document.getElementById(FORM_ID);

                if (!formEl?.contains(target)) {
                    return;
                }

                event.preventDefault();
                formEl.requestSubmit();
            }}
        >
            <internals.HeaderInfoText>
                {roleOptions.length > 1 ?
                    'Please choose the type of user and enter search criteria to match on.' :
                    'Please enter search criteria to match on.'}
            </internals.HeaderInfoText>
            {!!submitError && <Alert severity='error'>{submitError}</Alert>}
            {Form}
            <internals.ButtonRow>
                <Button
                    color='secondary'
                    onClick={onRequestClose}
                    type='button'
                    variant='outlined'
                >
                    Close
                </Button>
                <Button
                    color='secondary'
                    onClick={clearSearch}
                    type='button'
                    variant='outlined'
                >
                    Clear
                </Button>
                <Button
                    color='primary'
                    form={FORM_ID}
                    type='submit'
                    variant='contained'
                >
                    Search
                </Button>
            </internals.ButtonRow>
        </internals.FormWrapper>
    </FullScreenDialog>;
};

module.exports.propTypes = {
    activeSearchFilter: T.object,
    allRoles: T.array,
    availableRoleGroups: T.array,
    badges: T.array,
    canSearchHometown: T.bool,
    canSearchHousing: T.bool,
    classId: T.number,
    currentUserPermissions: T.object,
    currentUserRole: T.object,
    departments: T.array,
    discoverSearchFilter: T.object,
    discoverSortType: T.string,
    fieldNames: T.array,
    graduatingClassList: T.array,
    includeContextFilterType: T.bool,
    incomingClassList: T.array,
    interests: T.array,
    isBlankSearch: T.bool,
    isCommunity: T.bool,
    isCompany: T.bool,
    isSchoolOnline: T.bool,
    majorAsArray: T.bool,
    majors: T.array,
    officesList: T.array,
    onRequestClose: T.func.isRequired,
    onSubmit: T.func.isRequired,
    open: T.bool.isRequired,
    resetSearch: T.func.isRequired,
    roleGroupFilter: T.string,
    schoolRoles: T.array,
    yearsHiredList: T.array
};

internals.fieldProps = {};
internals.defaultSearchPayloadKeys = new Set([
    'classId',
    'contextFilterType'
]);

internals.asArray = (value) => Array.isArray(value) ? value : [];

internals.CUSTOM_LOCATION_FIELD_NAMES = ['customLocation', 'customLocationText'];

internals.clearCustomLocationWhenInactive = ({
    methods,
    selectedLocationType
} = {}) => {

    if (!methods || selectedLocationType === 'custom') {
        return;
    }

    methods.clearErrors?.(internals.CUSTOM_LOCATION_FIELD_NAMES);
    methods.setValue?.('customLocation', null, {
        shouldDirty: false,
        shouldValidate: false
    });
    methods.setValue?.('customLocationText', '', {
        shouldDirty: false,
        shouldValidate: false
    });
    methods.unregister?.(internals.CUSTOM_LOCATION_FIELD_NAMES);
};

internals.buildDefaults = ({
    currentUserRole,
    interestsList = [],
    isBlankSearch,
    roleGroupFilter,
    roleOptions = [],
    searchFilter = {}
}) => {

    const safeInterestsList = internals.asArray(interestsList);
    const safeRoleOptions = internals.asArray(roleOptions);
    const defaultRoleGroup = internals.getDefaultRoleGroup({
        currentUserRole,
        roleGroupFilter,
        roleOptions: safeRoleOptions
    });

    const filter = isBlankSearch ? {} : (searchFilter || {});
    const major = Array.isArray(filter.major) ? filter.major[0] : filter.major;

    return {
        name: filter.name || '',
        age: filter.age || '',
        studentName: filter.studentName || '',
        major: major || '',
        interests: internals.getDefaultInterests(filter.interests, safeInterestsList),
        radius: filter.radius || '',
        locationType: filter.locationType || '',
        customLocation: filter.customLocation || null,
        customLocationText: filter.customLocation?.name || '',
        incomingClass: filter.incomingClass || '',
        graduatingClass: filter.graduatingClass || '',
        isTransfer: internals.defaultBool(filter.isTransfer),
        isVeteran: internals.defaultBool(filter.isVeteran),
        isOnline: internals.defaultBool(filter.isOnline),
        searchIsParent: internals.defaultBool(filter.searchIsParent),
        openSocial: internals.defaultBool(filter.openSocial),
        departmentId: filter.departmentId || '',
        officeId: filter.officeId || '',
        yearHiredId: filter.yearHiredId || '',
        workRemote: internals.defaultBool(filter.workRemote),
        fullTimeStatus: filter.fullTimeStatus || '',
        badge: filter.badge || '',
        career: filter.career || '',
        profession: filter.profession || '',
        bio: filter.bio || '',
        title: filter.title || '',
        contextFilter: filter.contextFilter || defaultRoleGroup?.name || ''
    };
};

internals.getDefaultInterests = (interests = [], interestsList = []) => {

    const safeInterestsList = internals.asArray(interestsList);

    if (!Array.isArray(interests)) {
        return [];
    }

    if (!interests.length || typeof interests[0] === 'object') {
        return interests;
    }

    return safeInterestsList.filter(({ id }) => interests.includes(id));
};

internals.defaultBool = (value) => {

    if (value === true || value === false) {
        return value;
    }

    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return '';
};

internals.getDefaultRoleGroup = ({ currentUserRole, roleGroupFilter, roleOptions = [] }) => {

    const safeRoleOptions = internals.asArray(roleOptions);

    if (!safeRoleOptions.length) {
        return null;
    }

    if (roleGroupFilter && roleGroupFilter !== 'all') {
        const selectedFilter = safeRoleOptions.find(({ name }) => name === roleGroupFilter);

        if (selectedFilter) {
            return selectedFilter;
        }
    }

    const currentRole = safeRoleOptions.find(({ name, id }) => name === currentUserRole?.name || id === currentUserRole?.id);

    if (currentRole) {
        return currentRole;
    }

    return safeRoleOptions.find(({ name }) => name === 'student') || safeRoleOptions[0];
};

internals.buildRoleContext = ({ allRoles, badges, departments, roleOptions, selectedRoleGroup, usesRoleGroups }) => {

    const safeRoleOptions = internals.asArray(roleOptions);
    const safeAllRoles = internals.asArray(allRoles).filter(Boolean);
    const safeBadges = internals.asArray(badges);
    const safeDepartments = internals.asArray(departments);
    const selectedOption = safeRoleOptions.find(({ name }) => name === selectedRoleGroup);
    const roles = usesRoleGroups && selectedOption ?
        safeAllRoles.filter(({ roleGroupId }) => roleGroupId === selectedOption.id) :
        [selectedOption].filter(Boolean);
    const roleIds = roles.map(({ id }) => id);
    const canEdit = (permission) => roles.some((role) => role.permissions?.[permission]);

    return {
        isParent: selectedRoleGroup === 'parent',
        canEdit,
        badges: safeBadges.filter((badge) => badge?.used === true && roleIds.includes(badge.roleId)),
        departments: safeDepartments.filter((department) => {

            return department?.used === true && internals.asArray(department.usedBy).some((roleId) => roleIds.includes(roleId));
        })
    };
};

internals.getValidationErrors = (values) => {

    if (values.locationType === 'custom' && !values.customLocation?.placeId) {
        return [{
            field: 'customLocation',
            message: 'Please select a valid custom location from the dropdown.'
        }];
    }

    if (values.locationType && !values.radius) {
        return [{
            field: 'radius',
            message: 'Please select a distance when searching by location.'
        }];
    }

    return [];
};

internals.applyValidationErrors = ({
    formEl,
    methods,
    validationErrors = []
}) => InvalidSubmitFocusUtils.applySubmitErrors({
    errors: validationErrors,
    formEl,
    methods
});

internals.buildSearchPayload = ({ classId, includeContextFilterType, majorAsArray, usesRoleGroups, values }) => {

    const major = internals.toNumber(values.major);
    const payload = {
        name: values.name,
        age: internals.toNumber(values.age),
        studentName: values.studentName,
        major: values.major ? (majorAsArray ? [major] : major) : undefined,
        interests: values.interests,
        radius: internals.toNumber(values.radius),
        locationType: values.locationType,
        customLocation: values.locationType === 'custom' ? values.customLocation : undefined,
        incomingClass: values.incomingClass,
        graduatingClass: values.graduatingClass,
        isTransfer: internals.toBool(values.isTransfer),
        isVeteran: internals.toBool(values.isVeteran),
        isOnline: internals.toBool(values.isOnline),
        searchIsParent: internals.toBool(values.searchIsParent),
        openSocial: internals.toBool(values.openSocial),
        departmentId: internals.toNumber(values.departmentId),
        officeId: internals.toNumber(values.officeId),
        yearHiredId: internals.toNumber(values.yearHiredId),
        workRemote: internals.toBool(values.workRemote),
        fullTimeStatus: values.fullTimeStatus,
        badge: values.badge,
        career: values.career,
        profession: values.profession,
        bio: values.bio,
        title: values.title,
        contextFilter: values.contextFilter,
        contextFilterType: includeContextFilterType && values.contextFilter
            ? (usesRoleGroups ? 'roleGroup' : 'role')
            : undefined,
        classId
    };

    if (payload.radius && !payload.locationType) {
        delete payload.radius;
    }

    if (payload.locationType && !payload.radius) {
        delete payload.locationType;
        delete payload.customLocation;
    }

    if (payload.locationType !== 'custom') {
        delete payload.customLocation;
    }

    return internals.removeEmpty(payload);
};

internals.isDefaultSearchPayload = (payload = {}) => Object.keys(payload)
    .filter((key) => !internals.defaultSearchPayloadKeys.has(key))
    .length === 0;

internals.toBool = (value) => {

    if (value === true || value === 'true') {
        return true;
    }

    if (value === false || value === 'false') {
        return false;
    }

    return undefined;
};

internals.toNumber = (value) => {

    if (value === '' || value === null || typeof value === 'undefined') {
        return undefined;
    }

    const numberValue = Number(value);

    return Number.isNaN(numberValue) ? undefined : numberValue;
};

internals.removeEmpty = (obj) => Object.keys(obj).reduce((collector, key) => {

    const value = obj[key];

    if (value === '' || value === null || typeof value === 'undefined') {
        return collector;
    }

    if (typeof value === 'string' && value.trim().length === 0) {
        return collector;
    }

    if (Array.isArray(value) && value.length === 0) {
        return collector;
    }

    collector[key] = value;
    return collector;
}, {});

internals.FormWrapper = Styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
`;

internals.HeaderInfoText = Styled.p`
    margin: 0;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 16px;
    line-height: 22px;
`;

internals.ButtonRow = Styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
`;

internals.LocationHeader = Styled.h3`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    width: fit-content;
    margin: 8px auto 4px;
    padding: 0 12px;
    border-radius: 999px;
    background: rgba(18, 39, 79, 0.06);
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 18px;
    line-height: 22px;
    font-weight: 800;
    letter-spacing: -0.015em;
    text-align: center;
`;

module.exports.internals = internals;
